import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { useOnboardingState } from '@/contexts/OnboardingContext';
import { CAPS } from '@/lib/kbCaps';
import { extractKbFieldErrors, type FieldErrorMap } from '@/lib/kbFieldErrors';
import { FieldError } from '@/components/onboarding/enrichment/FieldError';
import { HIT_AREA_44 } from '@/lib/touchTarget';
import { cn } from '@/lib/utils';
import type { SlotDefinition } from '@/services/kbProfileService';

const MAX_SLOTS = CAPS.slot_definitions_items;
/** Base types always available. `session` is appended conditionally (FR3). */
const BASE_SLOT_TYPES: SlotDefinition['type'][] = [
  'text',
  'phone',
  'email',
  'date',
  'time',
  'number',
  'choice',
];

/**
 * The slot types offered in the type <Select>, gated on the schedule grid:
 * `session` is only selectable when the tenant has ≥1 session configured (FR3).
 * Single source of truth so every call site (the enrichment dialog AND the
 * wizard's inline qualification block) stays in lockstep — duplicating the gate
 * once let `session` silently vanish from the wizard.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared gate, not a component
export function availableSlotTypes(hasSessions: boolean): SlotDefinition['type'][] {
  return hasSessions ? [...BASE_SLOT_TYPES, 'session'] : BASE_SLOT_TYPES;
}

/** Auto-slug a human label → snake_case key. */
function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '') // strip accents
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

/**
 * Slugify `label`, then disambiguate against the keys already in use by the
 * OTHER rows of `slots` (i.e. every row except `selfIndex`) by appending
 * `-2`, `-3`, … until the result is unique (F171 FR5). Two labels that
 * slugify identically used to collide last-wins in the engine's `defs_map`
 * and in the generated JSON-schema `properties`, silently losing one of the
 * owner's fields — the backend now 422s on a duplicate key, so this is the
 * UX-side guard that keeps owners out of that error.
 *
 * Pure and exported so both call sites — the enrichment `SlotsEditor` dialog
 * and the wizard's inline qualification block via `SlotFieldsList` — share
 * the same policy instead of forking it (F077).
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared pure helper, not a component
export function uniqueSlugify(label: string, slots: SlotDefinition[], selfIndex: number): string {
  const base = slugify(label);
  if (!base) return base;
  const taken = new Set(slots.filter((_, i) => i !== selfIndex).map((s) => s.key));
  if (!taken.has(base)) return base;
  let n = 2;
  while (taken.has(`${base}-${n}`)) {
    n += 1;
  }
  return `${base}-${n}`;
}

// eslint-disable-next-line react-refresh/only-export-components -- shared with the wizard's inline slots block (F077)
export function makeEmptySlot(): SlotDefinition {
  return {
    key: '',
    label: '',
    type: 'text',
    required: false,
  };
}

/**
 * Strip the F171 FR6 dead-config keys (`destination` / `validation`) before
 * a save. `SlotDefinition` no longer declares either field, but a legacy
 * profile fetched from the API can still carry them on the wire — the
 * editor must not echo them back. Strip on the way OUT (here), not on the
 * way in, so loading a legacy profile and saving it untouched cleans it up.
 */
// eslint-disable-next-line react-refresh/only-export-components -- shared pure helper, not a component
export function stripDeadSlotKeys(slot: SlotDefinition): SlotDefinition {
  const {
    destination: _destination,
    validation: _validation,
    ...rest
  } = slot as SlotDefinition & {
    destination?: unknown;
    validation?: unknown;
  };
  return rest;
}

interface SlotFieldsListProps {
  slots: SlotDefinition[];
  onChange: (slots: SlotDefinition[]) => void;
  fieldErrors: FieldErrorMap | null;
  /** Available slot types to offer in the type <Select>. Defaults to BASE_SLOT_TYPES. */
  availableTypes?: SlotDefinition['type'][];
}

/** Controlled slot-definition row list (F077): the field UI shared by the
 * enrichment SlotsEditor dialog and the wizard's inline qualification block
 * (ObjectiveStep family 1) — reuse, never fork. */
export function SlotFieldsList({
  slots,
  onChange,
  fieldErrors,
  availableTypes = BASE_SLOT_TYPES,
}: SlotFieldsListProps) {
  const { t } = useTranslation();

  function updateSlot(idx: number, patch: Partial<SlotDefinition>) {
    onChange(
      slots.map((s, i) => {
        if (i !== idx) return s;
        const updated = { ...s, ...patch };
        // Auto-generate key from label if user hasn't customized it or key
        // currently matches the old auto-generated form. F171 FR5: disambiguate
        // against every OTHER row's key so two identical labels never collide.
        if (patch.label !== undefined) {
          updated.key = uniqueSlugify(patch.label, slots, idx);
        }
        return updated;
      })
    );
  }

  function addSlot() {
    if (slots.length < MAX_SLOTS) {
      onChange([...slots, makeEmptySlot()]);
    }
  }

  function removeSlot(idx: number) {
    onChange(slots.filter((_, i) => i !== idx));
  }

  // Blank-label slots are dropped from the payload, so the backend's list
  // index (the 422 path `slot_definitions.1.label`) can differ from the UI's.
  function payloadIndex(uiIndex: number): number {
    return slots.slice(0, uiIndex + 1).filter((s) => s.label.trim()).length - 1;
  }

  return (
    <>
      {slots.map((slot, idx) => (
        <div key={idx} className="space-y-3 rounded-md border p-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">
              {t('bot.enrich.slots.slot_label', { n: idx + 1 })}
            </span>
            {slots.length > 1 && (
              <Button
                variant="ghost"
                size="icon"
                aria-label={t('bot.enrich.slots.remove')}
                className={cn('h-7 w-7', HIT_AREA_44)}
                onClick={() => removeSlot(idx)}
              >
                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
              </Button>
            )}
          </div>
          {/* grid-cols-1 on mobile: a fixed 2-col grid blows past the card
              border in narrow containers (wizard inline block at 375px) —
              inputs/selects can't shrink below their intrinsic min width.
              min-w-0 lets each column shrink inside tight flex/grid parents. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor={`slot-label-${idx}`}>{t('bot.enrich.slots.label_field')}</Label>
              <Input
                id={`slot-label-${idx}`}
                value={slot.label}
                onChange={(e) => updateSlot(idx, { label: e.target.value })}
                placeholder={t('bot.enrich.slots.label_placeholder')}
                maxLength={CAPS.slot_label}
                className="min-h-11"
              />
              <FieldError
                errors={fieldErrors}
                path={`slot_definitions.${payloadIndex(idx)}.label`}
              />
            </div>
            <div className="space-y-1.5 min-w-0">
              <Label htmlFor={`slot-type-${idx}`}>{t('bot.enrich.slots.type_field')}</Label>
              <Select
                value={slot.type}
                onValueChange={(v) => updateSlot(idx, { type: v as SlotDefinition['type'] })}
              >
                <SelectTrigger id={`slot-type-${idx}`} className="min-h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Always include the slot's current type so an existing
                      `session` slot stays selectable even when the grid is
                      empty (availableTypes would otherwise omit `session`). */}
                  {(slot.type && !availableTypes.includes(slot.type)
                    ? [...availableTypes, slot.type]
                    : availableTypes
                  ).map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`bot.enrich.slots.type_${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {slot.type === 'choice' && (
            <div className="space-y-1.5">
              <Label htmlFor={`slot-choices-${idx}`}>{t('bot.enrich.slots.choices_label')}</Label>
              <Input
                id={`slot-choices-${idx}`}
                value={(slot.choices ?? []).join(', ')}
                onChange={(e) =>
                  updateSlot(idx, {
                    choices: e.target.value
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean),
                  })
                }
                placeholder={t('bot.enrich.slots.choices_placeholder')}
                className="min-h-11"
              />
            </div>
          )}
          {slot.type === 'session' && (
            <p className="text-sm text-muted-foreground">{t('bot.enrich.slots.session_hint')}</p>
          )}
          <div className="flex items-center gap-3">
            <Switch
              id={`slot-required-${idx}`}
              checked={slot.required}
              onCheckedChange={(checked) => updateSlot(idx, { required: checked })}
              aria-label={t('bot.enrich.slots.required_label')}
            />
            <Label htmlFor={`slot-required-${idx}`} className="cursor-pointer">
              {t('bot.enrich.slots.required_label')}
            </Label>
          </div>
        </div>
      ))}
      {slots.length < MAX_SLOTS && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addSlot}
          className="min-h-11 w-full"
        >
          <Plus className="h-4 w-4 mr-1" aria-hidden="true" />
          {t('bot.enrich.slots.add')}
        </Button>
      )}
    </>
  );
}

interface SlotsEditorProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialSlots: SlotDefinition[];
}

export function SlotsEditor({ open, onOpenChange, initialSlots }: SlotsEditorProps) {
  const { t } = useTranslation();
  const { updateKbProfile, kbProfile } = useOnboardingState();

  // FR3: `session` is only offered when the schedule grid has ≥1 session.
  const availableTypes = availableSlotTypes((kbProfile?.operation?.sessions?.length ?? 0) > 0);

  const [slots, setSlots] = useState<SlotDefinition[]>(
    initialSlots.length > 0 ? [...initialSlots] : [makeEmptySlot()]
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrorMap | null>(null);

  async function handleSave() {
    setSaving(true);
    setError(null);
    setFieldErrors(null);
    try {
      const valid = slots.filter((s) => s.label.trim()).map(stripDeadSlotKeys);
      await updateKbProfile({ slot_definitions: valid });
      onOpenChange(false);
    } catch (err) {
      const fe = extractKbFieldErrors(err);
      if (fe) {
        setFieldErrors(fe);
      } else {
        setError(t('common.error_generic'));
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{t('bot.enrich.slots.title')}</DialogTitle>
          <DialogDescription>{t('bot.enrich.slots.description')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-[min(60dvh,calc(100dvh-16rem))] overflow-y-auto pr-1">
          <SlotFieldsList
            slots={slots}
            onChange={setSlots}
            fieldErrors={fieldErrors}
            availableTypes={availableTypes}
          />
          {/* FR4: top-level slot_definitions error (e.g. orphaned session slot).
              The walk() flattener in extractKbFieldErrors maps the backend's
              `errors.json.slot_definitions = ["msg"]` to key `slot_definitions`.
              FieldError renders it inline; it appears near the footer so the
              owner sees it before clicking Save again. */}
          <FieldError errors={fieldErrors} path="slot_definitions" />
          {error && (
            <p className="text-sm text-destructive" role="alert">
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="min-h-11"
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} className="min-h-11">
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
